/* BOJOURNAL — wall-preview.js
   <wall-preview>: "Try it on your wall".
   1. A photo of the visitor's wall (or a sample room at a known scale).
   2. A line drawn along something on that wall whose length is known sets
      the scale: centimetres per pixel of this very photo.
   3. The work, cut from its photograph (Bojournal.artBox), is hung on the
      wall at width × height centimetres, with a soft shadow; it can be
      moved, its light matched to the room, and the picture saved/shared.
   Everything is drawn on one canvas, so what is saved is exactly what is
   seen. Points are kept in photo pixels, so resizing never shifts them.
   The photo never leaves the device. */
(() => {
  const B = window.Bojournal;
  if (!B || !B.Component || customElements.get('wall-preview')) return;

  const MAX_SIDE = 2400; // photos are reduced to this long side
  const HANDLE_HIT = 26; // CSS px around a handle that grabs it
  const LOUPE_R = 58; // CSS px
  const LOUPE_ZOOM = 3;
  const clamp = (n, min, max) => Math.min(max, Math.max(min, n));
  const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

  class WallPreview extends B.Component {
    init() {
      this.panel = this.closest('modal-panel');
      this.canvas = this.querySelector('[data-wp-canvas]');
      this.stage = this.querySelector('[data-wp-stage]');
      if (!this.canvas || !this.canvas.getContext) return;
      this.ctx = this.canvas.getContext('2d');
      try {
        this.t = JSON.parse(this.querySelector('[data-wall-i18n]').textContent);
      } catch (error) {
        this.t = {};
      }
      this.rtl = getComputedStyle(this).direction === 'rtl';
      this.size = { w: parseFloat(this.dataset.w), h: parseFloat(this.dataset.h) };
      this.photo = null; // canvas at working resolution
      this.art = null; // canvas: the painting alone
      this.artExportable = true;
      this.line = null; // { a: {x,y}, b: {x,y} } in photo px
      this.pxPerCm = 0;
      this.pos = null; // centre of the work, photo px
      this.light = 1;
      this.sample = false;
      this.drag = null;
      this.activeHandle = 'b';

      this.bindStart();
      this.bindMeasure();
      this.bindHang();
      this.bindPointer();
      this.bindKeys();

      if ('ResizeObserver' in window) {
        this.resizer = new ResizeObserver(() => this.draw());
        this.resizer.observe(this.stage);
      }
      this.on(window, 'resize', () => this.draw());
      if (this.panel) this.on(this.panel, 'panel:open', () => {
        this.loadArt();
        requestAnimationFrame(() => this.draw());
      });

      // Sharing a file is offered only where the browser can do it.
      try {
        const probe = new File([new Blob(['x'], { type: 'image/jpeg' })], 'x.jpg', { type: 'image/jpeg' });
        this.canShare = !!(navigator.canShare && navigator.canShare({ files: [probe] }));
      } catch (error) {
        this.canShare = false;
      }
    }

    teardown() {
      if (this.resizer) this.resizer.disconnect();
    }

    /* ---------------------------------------------------------------- steps */
    go(step) {
      this.dataset.step = String(step);
      this.querySelectorAll('[data-wp-panel]').forEach((panel) => (panel.hidden = panel.dataset.wpPanel !== String(step)));
      this.panel.querySelectorAll('[data-wp-dot]').forEach((dot) => {
        const n = Number(dot.dataset.wpDot);
        if (n === step) dot.setAttribute('aria-current', 'step');
        else dot.removeAttribute('aria-current');
        dot.classList.toggle('is-done', n < step);
        dot.classList.toggle('is-skipped', this.sample && n === 2);
      });
      this.querySelector('[data-wp-start]').hidden = step !== 1;
      this.canvas.hidden = step === 1;
      if (step === 2) this.canvas.setAttribute('aria-label', this.t.canvasMeasure || '');
      if (step === 3) this.canvas.setAttribute('aria-label', this.t.canvasHang || '');
      this.draw();
      if (step !== 1) {
        const heading = this.querySelector('[data-wp-panel="' + step + '"] .wallview__step-title');
        if (heading) {
          heading.setAttribute('tabindex', '-1');
          heading.focus({ preventScroll: true });
        }
      }
    }

    error(message) {
      const box = this.querySelector('[data-wp-error]');
      box.textContent = message || '';
      box.hidden = !message;
    }

    busy(on) {
      this.querySelector('[data-wp-busy]').hidden = !on;
    }

    /* --------------------------------------------------------- 1. the photo */
    bindStart() {
      const input = this.querySelector('[data-wp-file]');
      this.on(input, 'change', () => {
        if (input.files && input.files[0]) this.readFile(input.files[0]);
        input.value = '';
      });
      this.on(this.querySelector('[data-wp-sample]'), 'click', () => this.useSample());
      const drop = this.querySelector('[data-wp-drop]');
      this.on(drop, 'dragover', (event) => {
        event.preventDefault();
        drop.classList.add('is-over');
      });
      this.on(drop, 'dragleave', () => drop.classList.remove('is-over'));
      this.on(drop, 'drop', (event) => {
        event.preventDefault();
        drop.classList.remove('is-over');
        const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
        if (file) this.readFile(file);
      });
      this.querySelectorAll('[data-wp-restart]').forEach((button) =>
        this.on(button, 'click', () => {
          this.error('');
          this.go(1);
          const pick = this.querySelector('[data-wp-file]');
          if (pick) pick.focus({ preventScroll: true });
        })
      );
    }

    async readFile(file) {
      this.error('');
      if (file.type && !file.type.startsWith('image/')) {
        this.error(this.t.typeError);
        return;
      }
      this.busy(true);
      try {
        const source = await this.decode(file);
        this.photo = this.reduce(source.image, source.width, source.height);
        if (source.close) source.close();
        this.sample = false;
        this.pxPerCm = 0;
        this.pos = null;
        const w = this.photo.width;
        const h = this.photo.height;
        // A first line in the middle, a fifth of the photo long.
        this.line = { a: { x: w * 0.4, y: h * 0.5 }, b: { x: w * 0.6, y: h * 0.5 } };
        this.activeHandle = 'b';
        this.loadArt();
        this.go(2);
        this.status('[data-wp-measure-status]', Math.min(w, h) < 600 ? this.t.lowRes : '');
      } catch (error) {
        this.error(file.type === 'image/heic' || /\.hei[cf]$/i.test(file.name || '') ? this.t.typeError : this.t.loadError);
      } finally {
        this.busy(false);
      }
    }

    /* Decodes a file with its camera orientation applied. */
    async decode(file) {
      if (window.createImageBitmap) {
        try {
          const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
          return { image: bitmap, width: bitmap.width, height: bitmap.height, close: () => bitmap.close && bitmap.close() };
        } catch (error) {
          /* fall back to an <img> below */
        }
      }
      const url = URL.createObjectURL(file);
      try {
        const img = new Image();
        img.decoding = 'async';
        img.src = url;
        await img.decode();
        return { image: img, width: img.naturalWidth, height: img.naturalHeight };
      } finally {
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
    }

    reduce(image, width, height) {
      if (!width || !height) throw new Error('empty');
      const k = Math.min(1, MAX_SIDE / Math.max(width, height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(width * k);
      canvas.height = Math.round(height * k);
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
      return canvas;
    }

    /* A sample room drawn at 5 px per cm: a wall at least 480 cm wide and
       270 cm high (larger when the work needs it), floor, a 214 cm sofa and
       a floor lamp. The scale is known, so no measuring. */
    useSample() {
      this.error('');
      const k = 5;
      // Centre at 150 cm, or 25 cm above the sofa back if higher.
      const centreCm = Math.max(150, 84 + 25 + this.size.h / 2);
      const wallCm = Math.max(270, Math.ceil(centreCm + this.size.h / 2 + 40));
      const widthCm = Math.max(480, Math.ceil(this.size.w + 200));
      const W = widthCm * k;
      const floorY = wallCm * k;
      const H = floorY + 50 * k;
      const canvas = document.createElement('canvas');
      canvas.width = W;
      canvas.height = H;
      const ctx = canvas.getContext('2d');
      const wall = ctx.createLinearGradient(0, 0, 0, floorY);
      wall.addColorStop(0, '#d9d3c8');
      wall.addColorStop(1, '#ebe6de');
      ctx.fillStyle = wall;
      ctx.fillRect(0, 0, W, floorY);
      const glow = ctx.createRadialGradient(W * 0.5, 0, 0, W * 0.5, 0, W * 0.55);
      glow.addColorStop(0, 'rgba(255,250,240,0.55)');
      glow.addColorStop(1, 'rgba(255,250,240,0)');
      ctx.fillStyle = glow;
      ctx.fillRect(0, 0, W, floorY);
      ctx.fillStyle = '#f3f0ea';
      ctx.fillRect(0, floorY - 8 * k, W, 8 * k); // skirting
      const floor = ctx.createLinearGradient(0, floorY, 0, H);
      floor.addColorStop(0, '#b9ae9b');
      floor.addColorStop(1, '#a39781');
      ctx.fillStyle = floor;
      ctx.fillRect(0, floorY, W, H - floorY);
      ctx.strokeStyle = 'rgba(0,0,0,0.06)';
      ctx.lineWidth = 2;
      for (let x = 0; x < W; x += 120 * k) {
        ctx.beginPath();
        ctx.moveTo(x, floorY);
        ctx.lineTo(x - 40 * k, H);
        ctx.stroke();
      }
      const round = (x, y, w, h, r, fill) => {
        ctx.fillStyle = fill;
        ctx.beginPath();
        if (ctx.roundRect) ctx.roundRect(x, y, w, h, r);
        else ctx.rect(x, y, w, h);
        ctx.fill();
      };
      // Sofa, 214 cm, centred.
      const sx = (W - 214 * k) / 2;
      ctx.fillStyle = 'rgba(40,30,20,0.18)';
      ctx.beginPath();
      ctx.ellipse(W / 2, floorY + 3 * k, 118 * k, 6 * k, 0, 0, Math.PI * 2);
      ctx.fill();
      round(sx + 10 * k, floorY - 84 * k, 194 * k, 48 * k, 10 * k, '#9f968a');
      round(sx, floorY - 50 * k, 214 * k, 32 * k, 8 * k, '#958b7e');
      round(sx - 2 * k, floorY - 62 * k, 24 * k, 44 * k, 9 * k, '#8a8074');
      round(sx + 192 * k, floorY - 62 * k, 24 * k, 44 * k, 9 * k, '#8a8074');
      round(sx + 16 * k, floorY - 18 * k, 4 * k, 18 * k, 1 * k, '#4d453c');
      round(sx + 194 * k, floorY - 18 * k, 4 * k, 18 * k, 1 * k, '#4d453c');
      round(sx + 30 * k, floorY - 72 * k, 44 * k, 30 * k, 8 * k, '#b3aa9c'); // cushion
      round(sx + 140 * k, floorY - 72 * k, 44 * k, 30 * k, 8 * k, '#a9a092');
      // Floor lamp, 160 cm.
      const lx = sx + 214 * k + 55 * k;
      round(lx - 1 * k, floorY - 150 * k, 2 * k, 150 * k, 1 * k, '#3a342e');
      round(lx - 14 * k, floorY - 2 * k, 28 * k, 2 * k, 1 * k, '#3a342e');
      ctx.fillStyle = '#efe7d8';
      ctx.beginPath();
      ctx.moveTo(lx - 16 * k, floorY - 140 * k);
      ctx.lineTo(lx + 16 * k, floorY - 140 * k);
      ctx.lineTo(lx + 11 * k, floorY - 165 * k);
      ctx.lineTo(lx - 11 * k, floorY - 165 * k);
      ctx.closePath();
      ctx.fill();

      this.photo = canvas;
      this.sample = true;
      this.pxPerCm = k;
      this.line = null;
      this.pos = { x: W / 2, y: floorY - centreCm * k };
      this.loadArt();
      this.go(3);
      this.status('[data-wp-hang-status]', this.t.sampleNote);
    }

    /* ------------------------------------------------------ 2. measuring */
    bindMeasure() {
      const custom = this.querySelector('[data-wp-custom]');
      const wrap = this.querySelector('[data-wp-custom-wrap]');
      this.querySelectorAll('[data-wp-ref]').forEach((radio) =>
        this.on(radio, 'change', () => {
          wrap.hidden = radio.value !== 'custom' || !radio.checked;
          if (!wrap.hidden) custom.focus();
          this.status('[data-wp-measure-status]', '');
          this.draw();
        })
      );
      this.on(custom, 'input', () => {
        this.status('[data-wp-measure-status]', '');
        this.draw();
      });
      this.on(this.querySelector('[data-wp-next]'), 'click', () => this.confirmScale());
      this.on(this.querySelector('[data-wp-remeasure]'), 'click', () => {
        if (this.sample || !this.photo) {
          this.go(1);
          return;
        }
        this.go(2);
      });
    }

    get refCm() {
      const checked = this.querySelector('[data-wp-ref]:checked');
      if (!checked) return 0;
      if (checked.value === 'custom') {
        const value = parseFloat(String(this.querySelector('[data-wp-custom]').value).replace(',', '.'));
        return value > 0 && value <= 1000 ? value : 0;
      }
      return parseFloat(checked.value);
    }

    confirmScale() {
      const cm = this.refCm;
      if (!cm) {
        this.status('[data-wp-measure-status]', this.t.noLength, true);
        this.querySelector('[data-wp-custom]').focus();
        return;
      }
      const view = this.view();
      const length = dist(this.line.a, this.line.b);
      if (length * view.s < 24) {
        this.status('[data-wp-measure-status]', this.t.shortLine, true);
        return;
      }
      const pxPerCm = length / cm;
      const workW = this.size.w * pxPerCm;
      const workH = this.size.h * pxPerCm;
      const pw = this.photo.width;
      const ph = this.photo.height;
      if (workW > pw * 3 || workH > ph * 3) {
        this.status('[data-wp-measure-status]', this.t.tooBig, true);
        return;
      }
      if (workW * view.s < 6 || workH * view.s < 6) {
        this.status('[data-wp-measure-status]', this.t.tooSmall, true);
        return;
      }
      this.pxPerCm = pxPerCm;
      // First placement: centred on the wall, a little above the middle,
      // kept inside the photo.
      if (!this.pos) this.pos = { x: pw / 2, y: ph * 0.42 };
      this.keepInside();
      this.go(3);
      const fits = workW <= pw && workH <= ph;
      this.status('[data-wp-hang-status]', fits ? this.t.scaleOk : this.t.tooBig);
    }

    status(selector, message, isError) {
      const box = this.querySelector(selector);
      if (!box) return;
      box.textContent = message || '';
      box.classList.toggle('is-error', !!isError);
    }

    /* ---------------------------------------------------- 3. hanging */
    bindHang() {
      const light = this.querySelector('[data-wp-light]');
      this.on(light, 'input', () => {
        this.light = clamp(parseFloat(light.value) / 100, 0.45, 1);
        this.draw();
      });
      this.on(this.querySelector('[data-wp-save]'), 'click', () => this.export('save'));
      const share = this.querySelector('[data-wp-share]');
      if (this.canShare) share.hidden = false;
      this.on(share, 'click', () => this.export('share'));
    }

    loadArt() {
      if (this.art || this.artLoading) return;
      this.artLoading = true;
      const src = this.dataset.src;
      const build = (img, readable) => {
        let box = null;
        if (readable && B.artBox) {
          try {
            box = B.artBox(img);
          } catch (error) {
            box = null;
          }
        }
        const nw = img.naturalWidth;
        const nh = img.naturalHeight;
        const sx = box ? box.x * nw : 0;
        const sy = box ? box.y * nh : 0;
        const sw = box ? box.w * nw : nw;
        const sh = box ? box.h * nh : nh;
        // The painting at its own size ratio; the photo is cropped (never
        // stretched) when its proportions differ from the stated size.
        const ratio = this.size.w / this.size.h;
        let cw = sw;
        let ch = sh;
        let cx = sx;
        let cy = sy;
        if (sw / sh > ratio) {
          cw = sh * ratio;
          cx = sx + (sw - cw) / 2;
        } else {
          ch = sw / ratio;
          cy = sy + (sh - ch) / 2;
        }
        const art = document.createElement('canvas');
        const scale = Math.min(1, 1600 / Math.max(cw, ch));
        art.width = Math.max(1, Math.round(cw * scale));
        art.height = Math.max(1, Math.round(ch * scale));
        const ctx = art.getContext('2d');
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, cx, cy, cw, ch, 0, 0, art.width, art.height);
        this.art = art;
        this.artExportable = readable;
        this.artLoading = false;
        this.draw();
      };
      const first = new Image();
      first.crossOrigin = 'anonymous';
      first.decoding = 'async';
      first.onload = () => build(first, true);
      first.onerror = () => {
        // Not readable across origins: shown, but the picture cannot be saved.
        const second = new Image();
        second.decoding = 'async';
        second.onload = () => build(second, false);
        second.onerror = () => {
          this.artLoading = false;
        };
        second.src = src;
      };
      first.src = src;
    }

    workRect() {
      const w = this.size.w * this.pxPerCm;
      const h = this.size.h * this.pxPerCm;
      return { x: this.pos.x - w / 2, y: this.pos.y - h / 2, w, h };
    }

    keepInside() {
      if (!this.pos || !this.photo) return;
      this.pos.x = clamp(this.pos.x, 0, this.photo.width);
      this.pos.y = clamp(this.pos.y, 0, this.photo.height);
    }

    /* ------------------------------------------------------------ view */
    view() {
      const rect = this.stage.getBoundingClientRect();
      const W = Math.max(1, rect.width);
      const H = Math.max(1, rect.height);
      if (!this.photo) return { W, H, s: 1, ox: 0, oy: 0 };
      const pad = 12;
      const s = Math.min((W - pad * 2) / this.photo.width, (H - pad * 2) / this.photo.height);
      return { W, H, s, ox: (W - this.photo.width * s) / 2, oy: (H - this.photo.height * s) / 2 };
    }

    toPhoto(event) {
      const rect = this.canvas.getBoundingClientRect();
      const v = this.view();
      return { x: (event.clientX - rect.left - v.ox) / v.s, y: (event.clientY - rect.top - v.oy) / v.s, sx: event.clientX - rect.left, sy: event.clientY - rect.top };
    }

    /* ------------------------------------------------------------ draw */
    draw() {
      if (!this.ctx || this.dataset.step === '1' || !this.photo) return;
      const v = this.view();
      const dpr = Math.min(2.5, window.devicePixelRatio || 1);
      const cw = Math.round(v.W * dpr);
      const ch = Math.round(v.H * dpr);
      if (this.canvas.width !== cw || this.canvas.height !== ch) {
        this.canvas.width = cw;
        this.canvas.height = ch;
      }
      this.canvas.style.width = v.W + 'px';
      this.canvas.style.height = v.H + 'px';
      const ctx = this.ctx;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, v.W, v.H);
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(this.photo, v.ox, v.oy, this.photo.width * v.s, this.photo.height * v.s);

      if (this.dataset.step === '2' && this.line) this.drawLine(ctx, v);
      if (this.dataset.step === '3' && this.pos && this.pxPerCm) {
        ctx.save();
        ctx.translate(v.ox, v.oy);
        ctx.scale(v.s, v.s);
        this.paintWork(ctx, this.pxPerCm);
        ctx.restore();
        if (this.drag && this.drag.kind === 'work') this.drawSizeTag(ctx, v);
      }
    }

    /* The work on the wall: shadow, painting, the room's light. Drawn in
       photo pixels, so the screen and the saved picture match exactly. */
    paintWork(ctx, pxPerCm) {
      const r = this.workRect();
      ctx.save();
      ctx.shadowColor = 'rgba(20, 14, 8, 0.42)';
      ctx.shadowBlur = 2.4 * pxPerCm;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 1.2 * pxPerCm;
      if (this.art) {
        ctx.drawImage(this.art, r.x, r.y, r.w, r.h);
      } else {
        ctx.fillStyle = '#6f675c';
        ctx.fillRect(r.x, r.y, r.w, r.h);
      }
      ctx.restore();
      // A hair of canvas edge catching the light at the top.
      ctx.fillStyle = 'rgba(255, 255, 255, 0.12)';
      ctx.fillRect(r.x, r.y, r.w, Math.max(0.6, 0.15 * pxPerCm));
      if (this.light < 1) {
        ctx.fillStyle = 'rgba(12, 10, 8, ' + (1 - this.light).toFixed(3) + ')';
        ctx.fillRect(r.x, r.y, r.w, r.h);
      }
    }

    drawLine(ctx, v) {
      const a = { x: v.ox + this.line.a.x * v.s, y: v.oy + this.line.a.y * v.s };
      const b = { x: v.ox + this.line.b.x * v.s, y: v.oy + this.line.b.y * v.s };
      ctx.save();
      ctx.lineCap = 'round';
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.55)';
      ctx.lineWidth = 5;
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2;
      ctx.stroke();
      // End ticks, perpendicular, so the ends read exactly.
      const len = Math.max(1, dist(a, b));
      const nx = -(b.y - a.y) / len;
      const ny = (b.x - a.x) / len;
      [a, b].forEach((p, i) => {
        const key = i === 0 ? 'a' : 'b';
        ctx.strokeStyle = 'rgba(0,0,0,0.55)';
        ctx.lineWidth = 4;
        ctx.beginPath();
        ctx.moveTo(p.x - nx * 10, p.y - ny * 10);
        ctx.lineTo(p.x + nx * 10, p.y + ny * 10);
        ctx.stroke();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 1.6;
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(p.x, p.y, 9, 0, Math.PI * 2);
        ctx.fillStyle = key === this.activeHandle ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.7)';
        ctx.fill();
        ctx.lineWidth = 1.5;
        ctx.strokeStyle = '#121211';
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(p.x, p.y, 1.6, 0, Math.PI * 2);
        ctx.fillStyle = '#121211';
        ctx.fill();
      });
      // The length it stands for, beside the middle of the line.
      const cm = this.refCm;
      if (cm) {
        const label = '\u2066' + (Math.round(cm * 10) / 10).toString() + '\u2069 ' + (this.t.cm || 'cm');
        const mx = (a.x + b.x) / 2 + nx * 22;
        const my = (a.y + b.y) / 2 + ny * 22;
        this.tag(ctx, label, mx, my);
      }
      if (this.drag && this.drag.kind === 'handle') this.drawLoupe(ctx, v, this.line[this.drag.key]);
      ctx.restore();
    }

    tag(ctx, text, x, y) {
      ctx.save();
      ctx.font = '600 12px ' + getComputedStyle(this).fontFamily;
      ctx.direction = this.rtl ? 'rtl' : 'ltr';
      const w = ctx.measureText(text).width + 14;
      ctx.fillStyle = 'rgba(18, 18, 17, 0.82)';
      ctx.beginPath();
      if (ctx.roundRect) ctx.roundRect(x - w / 2, y - 11, w, 22, 11);
      else ctx.rect(x - w / 2, y - 11, w, 22);
      ctx.fill();
      ctx.fillStyle = '#fff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, x, y + 0.5);
      ctx.restore();
    }

    drawSizeTag(ctx, v) {
      const r = this.workRect();
      const x = v.ox + (r.x + r.w / 2) * v.s;
      const y = v.oy + (r.y + r.h) * v.s + 20;
      // Numbers isolated left-to-right, so Arabic never reverses them.
      this.tag(ctx, '\u2066' + Math.round(this.size.w) + ' × ' + Math.round(this.size.h) + '\u2069 ' + (this.t.cm || 'cm'), x, Math.min(y, v.H - 14));
    }

    /* A magnifier beside the finger, so a point can be placed exactly. */
    drawLoupe(ctx, v, point) {
      const px = v.ox + point.x * v.s;
      const py = v.oy + point.y * v.s;
      let lx = px;
      let ly = py - LOUPE_R - 34;
      if (ly - LOUPE_R < 4) ly = py + LOUPE_R + 34;
      lx = clamp(lx, LOUPE_R + 4, v.W - LOUPE_R - 4);
      const srcR = LOUPE_R / (v.s * LOUPE_ZOOM);
      ctx.save();
      ctx.beginPath();
      ctx.arc(lx, ly, LOUPE_R, 0, Math.PI * 2);
      ctx.fillStyle = '#111';
      ctx.fill();
      ctx.clip();
      ctx.drawImage(this.photo, point.x - srcR, point.y - srcR, srcR * 2, srcR * 2, lx - LOUPE_R, ly - LOUPE_R, LOUPE_R * 2, LOUPE_R * 2);
      ctx.strokeStyle = 'rgba(255,255,255,0.9)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(lx - 12, ly);
      ctx.lineTo(lx + 12, ly);
      ctx.moveTo(lx, ly - 12);
      ctx.lineTo(lx, ly + 12);
      ctx.stroke();
      ctx.strokeStyle = 'rgba(0,0,0,0.6)';
      ctx.beginPath();
      ctx.arc(lx, ly, 2.2, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
      ctx.save();
      ctx.beginPath();
      ctx.arc(lx, ly, LOUPE_R, 0, Math.PI * 2);
      ctx.lineWidth = 3;
      ctx.strokeStyle = '#fff';
      ctx.stroke();
      ctx.lineWidth = 1;
      ctx.strokeStyle = 'rgba(0,0,0,0.35)';
      ctx.stroke();
      ctx.restore();
    }

    /* --------------------------------------------------------- pointer */
    bindPointer() {
      const c = this.canvas;
      this.on(c, 'pointerdown', (event) => {
        if (!this.photo || event.button > 0) return;
        const p = this.toPhoto(event);
        const v = this.view();
        if (this.dataset.step === '2' && this.line) {
          const hit = ['a', 'b']
            .map((key) => ({ key, d: Math.hypot(v.ox + this.line[key].x * v.s - p.sx, v.oy + this.line[key].y * v.s - p.sy) }))
            .sort((m, n) => m.d - n.d)[0];
          if (hit.d <= HANDLE_HIT * (event.pointerType === 'touch' ? 1.5 : 1)) {
            this.drag = { kind: 'handle', key: hit.key, dx: this.line[hit.key].x - p.x, dy: this.line[hit.key].y - p.y };
            this.activeHandle = hit.key;
          } else {
            // Grabbing the line elsewhere moves it whole.
            this.drag = { kind: 'line', last: p };
          }
        } else if (this.dataset.step === '3' && this.pos) {
          const r = this.workRect();
          const inside = p.x >= r.x && p.x <= r.x + r.w && p.y >= r.y && p.y <= r.y + r.h;
          // A small work is still easy to catch: tapping near it moves it too.
          this.drag = { kind: 'work', dx: this.pos.x - p.x, dy: this.pos.y - p.y, jump: !inside };
          if (!inside) {
            this.pos = { x: p.x, y: p.y };
            this.drag.dx = 0;
            this.drag.dy = 0;
            this.keepInside();
          }
        } else {
          return;
        }
        event.preventDefault();
        c.setPointerCapture(event.pointerId);
        c.classList.add('is-dragging');
        this.draw();
      });
      this.on(c, 'pointermove', (event) => {
        if (!this.drag) {
          if (this.dataset.step === '3' && this.pos && event.pointerType === 'mouse') {
            const p = this.toPhoto(event);
            const r = this.workRect();
            c.classList.toggle('is-over-work', p.x >= r.x && p.x <= r.x + r.w && p.y >= r.y && p.y <= r.y + r.h);
          }
          return;
        }
        const p = this.toPhoto(event);
        const W = this.photo.width;
        const H = this.photo.height;
        if (this.drag.kind === 'handle') {
          this.line[this.drag.key] = { x: clamp(p.x + this.drag.dx, 0, W), y: clamp(p.y + this.drag.dy, 0, H) };
        } else if (this.drag.kind === 'line') {
          const dx = p.x - this.drag.last.x;
          const dy = p.y - this.drag.last.y;
          const { a, b } = this.line;
          const mdx = clamp(dx, -Math.min(a.x, b.x), W - Math.max(a.x, b.x));
          const mdy = clamp(dy, -Math.min(a.y, b.y), H - Math.max(a.y, b.y));
          this.line = { a: { x: a.x + mdx, y: a.y + mdy }, b: { x: b.x + mdx, y: b.y + mdy } };
          this.drag.last = p;
        } else if (this.drag.kind === 'work') {
          this.pos = { x: p.x + this.drag.dx, y: p.y + this.drag.dy };
          this.keepInside();
        }
        this.draw();
      });
      const end = (event) => {
        if (!this.drag) return;
        this.drag = null;
        c.classList.remove('is-dragging');
        if (c.hasPointerCapture && c.hasPointerCapture(event.pointerId)) c.releasePointerCapture(event.pointerId);
        if (this.dataset.step === '2') this.status('[data-wp-measure-status]', '');
        this.draw();
      };
      this.on(c, 'pointerup', end);
      this.on(c, 'pointercancel', end);
    }

    /* ----------------------------------------------------------- keys */
    bindKeys() {
      this.on(this.canvas, 'keydown', (event) => {
        const step = this.dataset.step;
        const moves = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
        if (step === '2' && this.line && (event.key === ' ' || event.key === 'Enter')) {
          event.preventDefault();
          this.activeHandle = this.activeHandle === 'a' ? 'b' : 'a';
          this.draw();
          return;
        }
        if (!moves[event.key]) return;
        event.preventDefault();
        const [mx, my] = moves[event.key];
        const v = this.view();
        if (step === '2' && this.line) {
          const stepPx = (event.shiftKey ? 10 : 1) / v.s;
          const p = this.line[this.activeHandle];
          this.line[this.activeHandle] = { x: clamp(p.x + mx * stepPx, 0, this.photo.width), y: clamp(p.y + my * stepPx, 0, this.photo.height) };
        } else if (step === '3' && this.pos) {
          const stepPx = (event.shiftKey ? 10 : 1) * this.pxPerCm;
          this.pos = { x: this.pos.x + mx * stepPx, y: this.pos.y + my * stepPx };
          this.keepInside();
        }
        this.draw();
      });
    }

    /* ---------------------------------------------------------- export */
    async export(mode) {
      if (!this.photo || !this.pos) return;
      const out = document.createElement('canvas');
      out.width = this.photo.width;
      out.height = this.photo.height;
      const ctx = out.getContext('2d');
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(this.photo, 0, 0);
      this.paintWork(ctx, this.pxPerCm);
      // A quiet credit in the corner.
      const note = (this.dataset.title || '') + ' · \u2066' + Math.round(this.size.w) + ' × ' + Math.round(this.size.h) + '\u2069 ' + (this.t.cm || 'cm') + ' · ' + (this.dataset.shop || '');
      const fs = Math.max(12, Math.round(out.width / 90));
      ctx.font = '500 ' + fs + 'px ' + getComputedStyle(this).fontFamily;
      ctx.direction = this.rtl ? 'rtl' : 'ltr';
      ctx.textAlign = this.rtl ? 'right' : 'left';
      ctx.textBaseline = 'bottom';
      const tx = this.rtl ? out.width - fs : fs;
      ctx.fillStyle = 'rgba(0,0,0,0.35)';
      ctx.fillText(note, tx + 1, out.height - fs + 1);
      ctx.fillStyle = 'rgba(255,255,255,0.92)';
      ctx.fillText(note, tx, out.height - fs);

      let blob = null;
      try {
        blob = await new Promise((resolve, reject) => {
          try {
            out.toBlob((b) => (b ? resolve(b) : reject(new Error('empty'))), 'image/jpeg', 0.92);
          } catch (error) {
            reject(error);
          }
        });
      } catch (error) {
        this.status('[data-wp-hang-status]', this.t.noExport, true);
        return;
      }
      const name = 'bojournal-' + (this.dataset.handle || 'work') + '-on-my-wall.jpg';
      if (mode === 'share' && this.canShare) {
        try {
          const file = new File([blob], name, { type: 'image/jpeg' });
          await navigator.share({ files: [file], title: this.dataset.title, text: this.t.shareText || '' });
          return;
        } catch (error) {
          if (error && error.name === 'AbortError') return;
          /* otherwise save it instead */
        }
      }
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 4000);
      this.status('[data-wp-hang-status]', this.t.saved);
    }
  }

  B.define('wall-preview', WallPreview);

  // The button on the product page opens the panel.
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest && event.target.closest('[data-wall-open]');
    if (!trigger) return;
    const panel = document.getElementById('WallPreview');
    if (panel && panel.open) {
      event.preventDefault();
      panel.open(trigger);
    }
  });
})();
